/**
 * Daywell Image Reveal — independent modern adaptation of the visual method from:
 * Codrops / SamsungGrid (https://github.com/codrops/SamsungGrid)
 * Original demo: Copyright (c) Codrops; MIT.
 *
 * Ported to TypeScript from the supplied integration kit (logic unchanged).
 * Uses existing image frames: data-dw-reveal="right" or "down".
 */
interface RevealRecord {
  element: HTMLElement;
  curtain: HTMLSpanElement;
  delay: number;
  onEnd: ((event: AnimationEvent) => void) | null;
  timeoutId: number | null;
  loadTimeoutId: number | null;
  finished: boolean;
}

export function initDaywellImageReveals(root: Document | HTMLElement = document): () => void {
  if (typeof window === 'undefined' || !root?.querySelectorAll) return () => {};
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return () => {};
  }

  const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-dw-reveal="right"], [data-dw-reveal="down"]'));
  const groupCounts = new Map<string, number>();
  const instances: RevealRecord[] = [];
  let destroyed = false;

  function finish(record: RevealRecord) {
    if (record.finished) return;
    record.finished = true;
    if (record.timeoutId) clearTimeout(record.timeoutId);
    if (record.onEnd) record.curtain.removeEventListener('animationend', record.onEnd);
    record.element.dataset.dwRevealState = 'shown';
    record.curtain.remove();
  }

  function startWhenReady(record: RevealRecord) {
    if (destroyed || record.finished) return;
    const img = record.element.querySelector('img');
    if (!img) {
      finish(record);
      return;
    }
    const start = () => {
      if (destroyed || record.finished) return;
      // Broken images should not leave a permanent empty mask.
      if (!img.complete || img.naturalWidth === 0) {
        finish(record);
        return;
      }
      record.element.dataset.dwRevealState = 'running';
      record.onEnd = (event: AnimationEvent) => {
        if (event.target === record.curtain) finish(record);
      };
      record.curtain.addEventListener('animationend', record.onEnd);
      const fallback = record.element.dataset.dwRevealVariant === 'portrait' ? 1700 : 1450;
      record.timeoutId = window.setTimeout(() => finish(record), fallback + record.delay);
    };

    if (img.complete) {
      start();
      return;
    }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      img.removeEventListener('load', settle);
      img.removeEventListener('error', settle);
      if (record.loadTimeoutId) clearTimeout(record.loadTimeoutId);
      record.loadTimeoutId = null;
      start();
    };
    img.addEventListener('load', settle);
    img.addEventListener('error', settle);
    record.loadTimeoutId = window.setTimeout(settle, 1800);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const record = instances.find((item) => item.element === entry.target);
      if (!record) return;
      observer.unobserve(record.element);
      startWhenReady(record);
    });
  }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });

  nodes.forEach((element) => {
    const img = element.querySelector('img');
    if (!img) return;
    // Leave already-on-screen images visible. No flash on initial load or route restoration.
    const bounds = element.getBoundingClientRect();
    const alreadyVisible = bounds.top < window.innerHeight && bounds.bottom > 0;
    if (alreadyVisible) return;

    // Variant: explicit data-dw-reveal-variant, else auto by frame aspect ratio.
    let variant = element.dataset.dwRevealVariant;
    if (variant !== 'wide' && variant !== 'portrait') {
      variant = bounds.height > 0 && bounds.width / bounds.height >= 1.3 ? 'wide' : 'portrait';
      element.dataset.dwRevealVariant = variant;
    }
    const stagger = variant === 'portrait' ? 120 : 100;

    const group = element.dataset.dwRevealGroup || '';
    const groupOrder = group ? (groupCounts.get(group) || 0) : 0;
    if (group) groupCounts.set(group, groupOrder + 1);
    const declaredDelay = Number(element.dataset.dwRevealDelay);
    const delay = Number.isFinite(declaredDelay) && element.hasAttribute('data-dw-reveal-delay')
      ? Math.min(500, Math.max(0, declaredDelay))
      : Math.min(420, groupOrder * stagger);

    const curtain = document.createElement('span');
    curtain.className = 'dw-reveal-curtain';
    curtain.setAttribute('aria-hidden', 'true');
    element.classList.add('dw-reveal-frame');
    element.style.setProperty('--dw-reveal-delay', `${delay}ms`);
    // "under" layer: curtain sits directly above the photo but beneath overlay text/gradients.
    if (element.dataset.dwRevealLayer === 'under' && img.parentElement === element) {
      img.insertAdjacentElement('afterend', curtain);
    } else {
      element.appendChild(curtain);
    }
    element.dataset.dwRevealState = 'pending';
    instances.push({
      element, curtain, delay, onEnd: null, timeoutId: null, loadTimeoutId: null, finished: false,
    });
    observer.observe(element);
  });

  return function destroyDaywellImageReveals() {
    destroyed = true;
    observer.disconnect();
    instances.forEach((record) => {
      if (record.loadTimeoutId) clearTimeout(record.loadTimeoutId);
      finish(record);
      record.element.removeAttribute('data-dw-reveal-state');
      record.element.classList.remove('dw-reveal-frame');
      record.element.style.removeProperty('--dw-reveal-delay');
    });
  };
}